import { useEffect, useRef, useState } from 'react'

type UseTableScrollYOptions = {
   minHeight?: number
   bottomGap?: number
   enabled?: boolean
   dependencyKey?: string | number
   targetSelector?: string
}

const useTableScrollY = ({
   minHeight = 320,
   bottomGap = 16,
   enabled = true,
   dependencyKey,
   targetSelector = '.ant-table-body'
}: UseTableScrollYOptions = {}) => {
   const tableWrapperRef = useRef<HTMLDivElement | null>(null)
   const paginationRef = useRef<HTMLDivElement | null>(null)
   const [scrollY, setScrollY] = useState(minHeight)

   useEffect(() => {
      if (!enabled) return

      const updateTableHeight = () => {
         const tableBody = tableWrapperRef.current?.querySelector(targetSelector) as HTMLElement | null
         if (!tableBody) return

         const modalBody = tableWrapperRef.current?.closest('.ant-modal-body') as HTMLElement | null
         const containerBottom = modalBody?.getBoundingClientRect().bottom || window.innerHeight
         const paginationRect = paginationRef.current?.getBoundingClientRect()
         const paginationStyle = paginationRef.current ? window.getComputedStyle(paginationRef.current) : null
         const paginationMarginTop = parseFloat(paginationStyle?.marginTop || '0')
         const paginationMarginBottom = parseFloat(paginationStyle?.marginBottom || '0')
         const paginationHeight = paginationRect
            ? paginationRect.height + paginationMarginTop + paginationMarginBottom
            : 0
         const nextHeight = Math.max(
            containerBottom - tableBody.getBoundingClientRect().top - paginationHeight - bottomGap,
            minHeight
         )

         setScrollY((currentHeight) => (Math.abs(currentHeight - nextHeight) > 1 ? nextHeight : currentHeight))
      }

      const frame = window.requestAnimationFrame(updateTableHeight)
      window.addEventListener('resize', updateTableHeight)

      const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateTableHeight) : undefined
      if (tableWrapperRef.current) resizeObserver?.observe(tableWrapperRef.current)
      if (paginationRef.current) resizeObserver?.observe(paginationRef.current)

      return () => {
         window.cancelAnimationFrame(frame)
         window.removeEventListener('resize', updateTableHeight)
         resizeObserver?.disconnect()
      }
   }, [bottomGap, dependencyKey, enabled, minHeight, targetSelector])

   return { paginationRef, scrollY, tableWrapperRef }
}

export default useTableScrollY
